import React from 'react';
import { Building, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Client } from '../../types/client';
import { AccountAssignment } from './NewAccountReviewModal';
import { UserAssignmentRow, UserOption } from './UserAssignmentRow';
import { formatCurrency } from '../../utils/currencyUtils';

interface NewAccountRowProps {
  account: {
    account_string: string;
    transaction_count: number;
    total_commission: number;
  };
  assignment: AccountAssignment | undefined;
  isExpanded: boolean;
  clients: Client[];
  users: UserOption[];
  showCreateClient: boolean;
  newClientName: string;
  creatingClient: boolean;
  onToggleExpand: () => void;
  onSetClient: (clientId: number | null) => void;
  onOpenCreateClient: () => void;
  onCloseCreateClient: () => void;
  onChangeNewClientName: (value: string) => void;
  onCreateClient: () => void;
  onAddUser: () => void;
  onUpdateUser: (idx: number, field: 'user_id' | 'commission_percentage', value: number) => void;
  onRemoveUser: (idx: number) => void;
}

export const NewAccountReviewRow: React.FC<NewAccountRowProps> = ({
  account,
  assignment,
  isExpanded,
  clients,
  users,
  showCreateClient,
  newClientName,
  creatingClient,
  onToggleExpand,
  onSetClient,
  onOpenCreateClient,
  onCloseCreateClient,
  onChangeNewClientName,
  onCreateClient,
  onAddUser,
  onUpdateUser,
  onRemoveUser,
}) => (
  <div className="border rounded-lg overflow-hidden">
    <div
      className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
      onClick={onToggleExpand}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{account.account_string}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {account.transaction_count} trade{account.transaction_count !== 1 ? 's' : ''} &middot;{' '}
          {formatCurrency(account.total_commission)} commission
        </p>
      </div>
      <div className="flex items-center space-x-3">
        {assignment?.client_id ? (
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-medium">
            Assigned
          </span>
        ) : (
          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded font-medium">
            Unassigned
          </span>
        )}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </div>
    </div>

    {isExpanded && (
      <div className="p-4 space-y-4 border-t">
        <div>
          <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1.5">
            <Building className="h-4 w-4" />
            <span>Assign to Client</span>
          </label>
          <div className="flex space-x-2">
            <select
              value={assignment?.client_id || ''}
              onChange={(e) => onSetClient(e.target.value ? parseInt(e.target.value) : null)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select Client --</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.client_name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onOpenCreateClient}
              className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              title="Create new client"
            >
              + New
            </button>
          </div>

          {showCreateClient && (
            <div className="mt-2 p-3 border border-blue-200 rounded-lg bg-blue-50">
              <label className="block text-xs font-medium text-blue-900 mb-1">
                New client name
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => onChangeNewClientName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onCreateClient();
                    }
                  }}
                  placeholder="e.g. Tourmaline Capital"
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm"
                  autoFocus
                  disabled={creatingClient}
                />
                <button
                  type="button"
                  onClick={onCreateClient}
                  disabled={creatingClient || !newClientName.trim()}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingClient ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={onCloseCreateClient}
                  disabled={creatingClient}
                  className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1.5">
            <Users className="h-4 w-4" />
            <span>Assign Employees (optional)</span>
          </label>

          {assignment?.user_assignments.map((ua, idx) => (
            <UserAssignmentRow
              key={idx}
              ua={ua}
              users={users}
              onUpdate={(field, value) => onUpdateUser(idx, field, value)}
              onRemove={() => onRemoveUser(idx)}
            />
          ))}

          <button onClick={onAddUser} className="text-sm text-blue-600 hover:text-blue-800">
            + Add Employee
          </button>
        </div>
      </div>
    )}
  </div>
);
